import * as THREE from 'three';
import {
  FURNITURE_CATALOG,
  addFurniture,
  readFurnitureLayout,
  removeFurniture,
  rotateFurniture,
  writeFurnitureLayout,
} from '../data/furnitureLayout.js';
import { makeFurniture } from '../viewer/house/furniture.js';
import { clear, el } from './dom.js';

/** edit=1 専用。仮置き家具を床クリックで追加し、物件ごとにブラウザへ保存する。 */
export function createFurnitureEditor(container, { scene, camera, canvas, space, property }) {
  const layer = new THREE.Group();
  layer.name = `furniture-layout:${property.id}`;
  scene.add(layer);

  let layout = readFurnitureLayout(localStorage, property.id);
  let pendingType = null;
  const list = el('ol', { class: 'fe-list' });
  const status = el('p', { class: 'fe-status' }, '家具を選ぶと、次にクリックした床へ置きます。');

  const palette = el(
    'div',
    { class: 'fe-palette' },
    FURNITURE_CATALOG.map((item) =>
      el(
        'button',
        {
          class: 'fe-choice',
          type: 'button',
          onClick: () => {
            pendingType = item.type;
            status.textContent = `「${item.label}」を置く床をクリックしてください。`;
            renderPalette();
          },
          dataset: { type: item.type },
        },
        item.label,
      ),
    ),
  );

  function persistAndRender(next) {
    layout = next;
    writeFurnitureLayout(localStorage, property.id, layout);
    renderFurniture();
    renderList();
  }

  function renderFurniture() {
    while (layer.children.length > 0) {
      const child = layer.children.at(-1);
      child.traverse((object) => {
        object.geometry?.dispose?.();
        if (Array.isArray(object.material)) object.material.forEach((material) => material.dispose?.());
        else object.material?.dispose?.();
      });
      layer.remove(child);
    }
    for (const item of layout) {
      const object = makeFurniture(item);
      object.userData.furnitureId = item.id;
      layer.add(object);
    }
  }

  function renderPalette() {
    for (const button of palette.querySelectorAll('.fe-choice')) {
      button.classList.toggle('is-active', button.dataset.type === pendingType);
    }
  }

  function renderList() {
    clear(list);
    if (layout.length === 0) {
      list.appendChild(el('li', { class: 'fe-empty' }, '仮置き家具はありません'));
      return;
    }
    layout.forEach((item, index) => {
      const label = FURNITURE_CATALOG.find((entry) => entry.type === item.type)?.label ?? item.type;
      list.appendChild(
        el('li', {}, [
          el('span', {}, `${index + 1}. ${label}`),
          el('button', { class: 'fe-mini', type: 'button', onClick: () => persistAndRender(rotateFurniture(layout, item.id)) }, '↻ 90°'),
          el('button', { class: 'fe-mini fe-remove', type: 'button', onClick: () => persistAndRender(removeFurniture(layout, item.id)) }, '撤去'),
        ]),
      );
    });
  }

  const raycaster = new THREE.Raycaster();
  const pointer = new THREE.Vector2();
  const normalMatrix = new THREE.Matrix3();
  const worldNormal = new THREE.Vector3();

  function placeAt(event) {
    if (pendingType === null) return;
    event.preventDefault();
    event.stopPropagation();
    const rect = canvas.getBoundingClientRect();
    pointer.set(((event.clientX - rect.left) / rect.width) * 2 - 1, -((event.clientY - rect.top) / rect.height) * 2 + 1);
    raycaster.setFromCamera(pointer, camera);
    const hits = [];
    space.raycast(raycaster, hits);
    const floorHit =
      hits.find((hit) => {
        if (!hit.face || !hit.object) return true;
        normalMatrix.getNormalMatrix(hit.object.matrixWorld);
        return worldNormal.copy(hit.face.normal).applyMatrix3(normalMatrix).normalize().y > 0.55;
      }) ?? null;
    if (floorHit === null) {
      status.textContent = '床を選べませんでした。床面をもう一度クリックしてください。';
      return;
    }
    persistAndRender(addFurniture(layout, pendingType, floorHit.point));
    pendingType = null;
    status.textContent = '配置しました。回転や撤去は下の一覧から行えます。';
    renderPalette();
  }

  canvas.addEventListener('click', placeAt, true);
  renderFurniture();
  renderList();

  const root = el('aside', { class: 'fe-panel', 'aria-label': '家具レイアウト' }, [
    el('header', { class: 'fe-head' }, [el('strong', {}, '家具レイアウト'), el('span', {}, '自動保存')]),
    el('p', { class: 'fe-hint' }, '選択 → 床をクリック → 必要なら回転。ここで置いた家具だけ撤去できます。'),
    palette,
    status,
    list,
    el(
      'button',
      {
        class: 'btn fe-clear',
        type: 'button',
        onClick: () => {
          if (layout.length === 0 || !window.confirm('仮置きした家具をすべて撤去しますか？')) return;
          persistAndRender([]);
          status.textContent = '仮置き家具をすべて撤去しました。';
        },
      },
      'すべて撤去',
    ),
  ]);
  container.appendChild(root);

  return {
    get layout() {
      return layout;
    },
    dispose() {
      canvas.removeEventListener('click', placeAt, true);
      layer.removeFromParent();
      root.remove();
    },
  };
}
